// src/admin/screens/kyc/modals/ApproveKycModal.tsx
// Flips a pending/manual_review submission to verified, stamps the reviewer
// and reviewed_at, and clears review_notes. The borrower app reads
// profiles.kyc_status (mirrored by trigger) to decide whether to unlock
// the loan-apply screen, so this write is all it takes to let them borrow.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';

interface Props {
  kyc: { id: string; user_id: string; nrc_number: string | null };
  onClose: (refresh: boolean) => void;
}

export default function ApproveKycModal({ kyc, onClose }: Props) {
  const { admin } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from('kyc_submissions')
        .update({
          status: 'verified',
          reviewer_admin_id: admin.id,
          reviewed_at: new Date().toISOString(),
          // Stash the reviewer's internal note in review_notes so it shows
          // up in the audit trail even for approvals.
          review_notes: note || null,
        })
        .eq('id', kyc.id);
      if (error) throw error;
      await recordAudit({
        action: 'kyc.approve',
        target_type: 'kyc',
        target_id: kyc.id,
        payload: {
          borrower_id: kyc.user_id,
          nrc_number: kyc.nrc_number,
          note: note || undefined,
        },
      });
      toast({ kind: 'success', msg: 'KYC approved. Borrower can now apply for loans.' });
      await qc.invalidateQueries({ queryKey: ['kyc', kyc.id] });
      await qc.invalidateQueries({ queryKey: ['kyc-queue'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title="Approve KYC">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="small">
          This marks the submission as <strong>verified</strong>. The borrower will be able to
          apply for loans immediately.
        </p>
        <FormField label="Internal note (optional)" htmlFor="approve-kyc-note">
          <textarea
            id="approve-kyc-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Approve
          </Button>
        </div>
      </form>
    </Modal>
  );
}
